/*
 * Column Utility Functions
 * 
 * Features:
 * - Convert schema field definitions to AG-Grid column definitions
 * - Type-specific formatting and filtering configuration
 * - Consistent column behavior across table components
 * 
 * Implementation:
 * - Maps FormField types to AG-Grid ColDef properties
 * - Handles date, number, select, and text field types
 * - Provides proper value formatters and filters
 * 
 * Changes:
 * - 2026-01-28: Created for schema-based list view filtering
 * - 2026-02-24: Render account phone/website and contact email as plain text (no link anchors)
 * - 2026-02-24: Include numbered email fields (Email1/Email2/...) in plain text rendering
 * - 2026-04-28: Parse string-backed schema date fields explicitly so AG Grid date formatting and filtering work reliably
 * - 2026-08-04: Keep schema date-filter calendars open during month navigation
 * - 2026-08-06: Include exact From and To dates in schema Between filters
 * - 2026-08-12: Keep numeric filter controls right-aligned while preserving numeric cell alignment
 * - 2026-08-17: Display numerical ID fields without locale grouping separators
 * - 2026-08-25: Let textarea columns inherit the shared single-line ellipsis display contract
 */

import { ColDef } from 'ag-grid-community';
import { FormField } from '../components/common/FormField';
import { createEmailCellRenderer, createUrlCellRenderer, createTelCellRenderer } from './gridCellRenderers';
import { DateFilterInput } from '../components/common/DateFilterInput';

type GridDateValue = string | number | Date | null | undefined;

function parseGridDateValue(value: GridDateValue): Date | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      return null;
    }

    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  if (typeof value === 'number') {
    const parsedFromNumber = new Date(value);
    if (Number.isNaN(parsedFromNumber.getTime())) {
      return null;
    }

    return new Date(
      parsedFromNumber.getFullYear(),
      parsedFromNumber.getMonth(),
      parsedFromNumber.getDate()
    );
  }

  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return null;
  }

  const dateOnlyMatch = trimmedValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    const parsedDateOnly = new Date(Number(year), Number(month) - 1, Number(day));

    if (Number.isNaN(parsedDateOnly.getTime())) {
      return null;
    }

    return parsedDateOnly;
  }

  const parsedDateTime = new Date(trimmedValue);
  if (Number.isNaN(parsedDateTime.getTime())) {
    return null;
  }

  return new Date(
    parsedDateTime.getFullYear(),
    parsedDateTime.getMonth(),
    parsedDateTime.getDate()
  );
}

function formatGridDateValue(value: GridDateValue): string {
  const parsedDate = parseGridDateValue(value);
  if (!parsedDate) {
    return typeof value === 'string' ? value : '';
  }

  return parsedDate.toLocaleDateString();
}

function compareGridDateFilterValues(filterLocalDateAtMidnight: Date, cellValue: GridDateValue): number {
  const parsedDate = parseGridDateValue(cellValue);

  if (!parsedDate) {
    return 0;
  }

  const cellTime = parsedDate.getTime();
  const filterTime = filterLocalDateAtMidnight.getTime();

  if (cellTime === filterTime) {
    return 0;
  }

  return cellTime < filterTime ? -1 : 1;
}

function compareGridDateSortValues(valueA: GridDateValue, valueB: GridDateValue): number {
  const parsedDateA = parseGridDateValue(valueA);
  const parsedDateB = parseGridDateValue(valueB);

  if (!parsedDateA && !parsedDateB) {
    return 0;
  }

  if (!parsedDateA) {
    return -1;
  }

  if (!parsedDateB) {
    return 1;
  }

  return parsedDateA.getTime() - parsedDateB.getTime();
}

/**
 * Some list fields must stay plain text even if their schema type supports links.
 */
function shouldRenderAsPlainText(field: FormField): boolean {
  const normalizedName = (field.name || '').toLowerCase();
  const normalizedLabel = (field.label || '').toLowerCase();
  const isEmailVariant = /^email\d*$/.test(normalizedName) || /^email\d*$/.test(normalizedLabel.replace(/\s+/g, ''));

  return (
    normalizedName === 'phonenumber' ||
    normalizedName === 'website' ||
    normalizedName === 'email' ||
    normalizedLabel === 'phone number' ||
    normalizedLabel === 'website' ||
    normalizedLabel === 'email' ||
    isEmailVariant
  );
}

function isNumericalIdField(field: FormField): boolean {
  return [field.name, field.label].some(value => {
    if (!value) return false;

    const trimmedValue = value.trim();
    return (
      /^id$/i.test(trimmedValue) ||
      /[\s_-]id$/i.test(trimmedValue) ||
      /Id$/.test(trimmedValue) ||
      /[a-z0-9]ID$/.test(trimmedValue)
    );
  });
}

/** * Convert a schema field definition to an AG-Grid column definition
 * 
 * @param field - Field definition from schema JSON
 * @returns AG-Grid column definition with type-specific configuration
 */
export function fieldToColumnDef(field: FormField): ColDef {
  const base: ColDef = {
    field: field.name,
    headerName: field.label,
    filter: true,
    sortable: true,
    resizable: true,
    minWidth: 100,
  };

  switch (field.type) {
    case 'date':
      return {
        ...base,
        type: 'date',
        cellDataType: false,
        valueFormatter: (params) => {
          return formatGridDateValue(params.value as GridDateValue);
        },
        comparator: (valueA, valueB) => compareGridDateSortValues(valueA as GridDateValue, valueB as GridDateValue),
        filter: 'agDateColumnFilter',
        dateComponent: DateFilterInput,
        filterParams: {
          browserDatePicker: true,
          inRangeInclusive: true,
          comparator: (filterLocalDateAtMidnight: Date, cellValue: GridDateValue) => {
            return compareGridDateFilterValues(filterLocalDateAtMidnight, cellValue);
          },
          isValidDate: (cellValue: GridDateValue) => parseGridDateValue(cellValue) !== null,
        },
        minWidth: 120,
      };

    case 'number': {
      const isIdField = isNumericalIdField(field);

      return {
        ...base,
        type: 'number',
        cellClass: 'ag-right-aligned-cell',
        valueFormatter: (params) => {
          if (params.value === null || params.value === undefined) return '';
          if (isIdField) return String(params.value);

          try {
            return Number(params.value).toLocaleString();
          } catch {
            return params.value;
          }
        },
        filter: 'agNumberColumnFilter',
        minWidth: 100,
      };
    }

    case 'select':
      return {
        ...base,
        filter: 'agTextColumnFilter',
        minWidth: 120,
      };

    case 'email':
      if (shouldRenderAsPlainText(field)) {
        return {
          ...base,
          minWidth: 200,
        };
      }
      return {
        ...base,
        minWidth: 200,
        cellRenderer: createEmailCellRenderer(),
      };

    case 'url':
      if (shouldRenderAsPlainText(field)) {
        return {
          ...base,
          minWidth: 200,
        };
      }
      return {
        ...base,
        minWidth: 200,
        cellRenderer: createUrlCellRenderer(),
      };

    case 'tel':
      if (shouldRenderAsPlainText(field)) {
        return {
          ...base,
          minWidth: 150,
        };
      }
      return {
        ...base,
        minWidth: 150,
        cellRenderer: createTelCellRenderer(),
      };

    case 'boolean':
      return {
        ...base,
        minWidth: 80,
        cellRenderer: (params: any) => {
          return params.value ? '✓' : '';
        },
        filter: 'agTextColumnFilter',
      };

    case 'textarea':
      return {
        ...base,
        minWidth: 250,
        autoHeight: false,
      };

    default:
      // text, string, or unknown types
      return base;
  }
}

/**
 * Convert an array of schema fields to AG-Grid column definitions
 * 
 * @param fields - Array of field definitions from schema JSON
 * @returns Array of AG-Grid column definitions
 */
export function schemaFieldsToColumnDefs(fields: FormField[]): ColDef[] {
  return fields.map(field => fieldToColumnDef(field));
}
